import { oc } from "@orpc/contract";
import { z } from "zod";
import { OrderStage } from "@erp/shared-types";

/**
 * Order Approval Funnel report contract.
 *
 * Powers the Sales → Reports page. Returns, for the caller's organization:
 *  - a current-snapshot count of orders sitting in each funnel stage,
 *  - the average dwell time spent in each stage, and
 *  - the overall average cycle time from received to submitted-to-ERP.
 *
 * All values are computed from the `ar_order_stage_events` transition log via
 * aggregate queries (no row hydration).
 */

export const GetOrderStageReportInputSchema = z.object({
  /** Inclusive lower bound (YYYY-MM-DD) on when an order was RECEIVED. */
  from: z.string().optional(),
  /** Exclusive upper bound (YYYY-MM-DD) on when an order was RECEIVED. */
  to: z.string().optional(),
  /** Restrict to these staged-PO sources (email, edi, file_upload, ...). */
  sources: z.array(z.string()).optional(),
  /** Restrict to these customers. */
  customerIds: z.array(z.uuid()).optional(),
});

export const OrderStageFunnelRowSchema = z.object({
  stage: z.nativeEnum(OrderStage),
  /** Orders whose current (latest) stage is this one. */
  count: z.int().nonnegative(),
  /**
   * Average seconds spent in this stage before moving on, across orders that
   * have left it. Null when no order has yet completed this stage.
   */
  avgDwellSeconds: z.number().nonnegative().nullable(),
});

export const GetOrderStageReportResponseSchema = z.object({
  /** One row per funnel stage, in canonical funnel order. */
  stages: z.array(OrderStageFunnelRowSchema),
  /** Average seconds from RECEIVED to SUBMITTED_TO_ERP across submitted orders. */
  overallAvgSeconds: z.number().nonnegative().nullable(),
  /** Total orders in scope (sum of stage counts). */
  totalReceived: z.int().nonnegative(),
  /** ISO timestamp the snapshot was computed. */
  asOf: z.string(),
});

export const orderStageReportContracts = {
  get: oc
    .input(GetOrderStageReportInputSchema)
    .output(GetOrderStageReportResponseSchema)
    .route({ method: "GET" }),
} as const;

export type GetOrderStageReportInput = z.infer<
  typeof GetOrderStageReportInputSchema
>;
export type OrderStageFunnelRow = z.infer<typeof OrderStageFunnelRowSchema>;
export type GetOrderStageReportResponse = z.infer<
  typeof GetOrderStageReportResponseSchema
>;
